import type { PlacementGroup } from '@/modules/adv/ad-service'
import {
    INDEX_EXCHANGE,
    MEDIA_NET_MULTIFORMAT,
    MEDIA_NET_VIDEO,
    ONETAG,
    OMNIDEX_VIDEO,
    PUBMATIC_VIDEO,
    UNRULY,
    PLAYGAMA,
    PLAYGAMA_FALLBACK,
    TAPPX_VIDEO,
    LOOPME,
    ADITUDE_VIDEO,
    EQUATIV,
    APS,
    INMOBI_VIDEO,
} from '@/modules/adv/header-bidding/prebid/bidders-config'
import { type AdProvidersSettings, type GoogleAdSettings, type PlatformType, type EffectiveType } from '@/types'

import { getCommonProvidersAdSettings } from './common-providers-settings'
import { createHeaderBiddingAdSettings } from './provider-factories'
import { filterAdProviderSettings } from './utils'

const REWARDED_VIDEO_AUCTION_BIDDERS = [
    MEDIA_NET_VIDEO,
    PLAYGAMA,
    INMOBI_VIDEO,
    PUBMATIC_VIDEO,
    UNRULY,
    LOOPME,
    EQUATIV,
    INDEX_EXCHANGE,
    TAPPX_VIDEO,
    ONETAG,
    APS,
]

function withRewardedVideoAuction(settings: GoogleAdSettings, isUsExperiment: boolean): GoogleAdSettings {
    return {
        ...settings,
        options: {
            ...settings.options,
            rewardedVideoAuction: {
                bidders: isUsExperiment
                    ? REWARDED_VIDEO_AUCTION_BIDDERS.filter(({ bidder }) => !['unruly', 'loopme'].includes(bidder))
                    : REWARDED_VIDEO_AUCTION_BIDDERS,
                timeoutMs: isUsExperiment ? 800 : 1000,
            },
        },
    }
}

export function getPortalAdSettings({
    gamAccount,
    platform,
    effectiveType,
    isH5RewardedAuction,
    isPlaygamaBidderOnly,
    countryCode,
}: {
    gamAccount: string
    platform: PlatformType
    effectiveType: EffectiveType | undefined
    isH5RewardedAuction?: boolean
    isPlaygamaBidderOnly?: boolean
    countryCode?: string
}): PlacementGroup<AdProvidersSettings>[] {
    if (countryCode && ['MY', 'VN'].includes(countryCode.toUpperCase())) {
        return [{ placements: ['*'], settings: [createHeaderBiddingAdSettings(platform, [PLAYGAMA])] }]
    }

    const common = getCommonProvidersAdSettings(gamAccount, platform, isPlaygamaBidderOnly)

    const isUsExperiment = countryCode?.toUpperCase() === 'US'
    const auctionEnabled = isH5RewardedAuction && platform === 'desktop' && !(isUsExperiment && effectiveType === '3g')
    const fullscreenGroups: Array<{ placements: PlacementGroup<AdProvidersSettings>['placements']; auction: boolean }> =
        auctionEnabled
            ? [
                  { placements: isUsExperiment ? ['interstitial'] : ['interstitial', 'rewarded'], auction: true },
                  {
                      placements: isUsExperiment
                          ? ['interstitial_preroll', 'interstitial_midroll', 'rewarded']
                          : ['interstitial_preroll', 'interstitial_midroll'],
                      auction: false,
                  },
              ]
            : [
                  {
                      placements: ['interstitial', 'interstitial_midroll', 'interstitial_preroll', 'rewarded'],
                      auction: false,
                  },
              ]

    return [
        {
            placements: ['*'],
            settings: [common.GOOGLE_AD_PORTAL_SETTINGS, common.HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS],
        },
        ...fullscreenGroups.map(({ placements, auction }) => ({
            placements,
            settings: [
                ...filterAdProviderSettings(effectiveType, [
                    auction
                        ? withRewardedVideoAuction(common.GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT, isUsExperiment)
                        : common.GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT,
                    common.GOOGLE_INSTREAM_GAME_PAGE_SETTINGS,
                    common.GOOGLE_AD_GAME_PAGE_SETTINGS_INTERSTITIAL_FORMAT,
                    common.GOOGLE_BANNER_GAME_PAGE_SETTINGS,
                ]),
                createHeaderBiddingAdSettings(platform, [
                    TAPPX_VIDEO,
                    MEDIA_NET_MULTIFORMAT,
                    ONETAG,
                    ADITUDE_VIDEO,
                    OMNIDEX_VIDEO,
                    PUBMATIC_VIDEO,
                    UNRULY,
                    LOOPME,
                    INMOBI_VIDEO,
                    EQUATIV,
                    INDEX_EXCHANGE,
                    APS,
                    PLAYGAMA_FALLBACK,
                ]),
            ],
        })),
    ]
}
